import React, { useState, useEffect } from 'react';
import Select, { SingleValue, components, OptionProps } from 'react-select';
import { useTranslation } from 'react-i18next';
import { OfferDraftItem, OfferDraftOption } from '../../types/offerTypes';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import { reactSelectTokenStyles } from '../../styles/reactSelectTokenStyles';

interface OfferDraftSelectorProps {
  drafts: OfferDraftItem[];
  selectedDraftId: string | null;
  onSelectDraft: (selectedOption: SingleValue<OfferDraftOption>) => void;
  onDeleteDraft: (draftId: string) => void;
  isLoading: boolean;
  error: string | null;
}

// Erweiterte Option-Struktur für das Custom-Rendering
interface ExtendedDraftOption extends OfferDraftOption {
  draftData: OfferDraftItem;
}

const OfferDraftSelector: React.FC<OfferDraftSelectorProps> = ({
  drafts,
  selectedDraftId,
  onSelectDraft,
  onDeleteDraft,
  isLoading,
  error
}) => {
  const { t, i18n } = useTranslation('offer');
  // M11b-3: advisor (read-only) darf keine Angebots-Entwürfe löschen.
  const isReadOnly = useIsReadOnly();
  // Sortiere Drafts nach Datum (neueste zuerst)
  const sortedDrafts = [...drafts].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.lastModified || a.createdDate || 0);
    const dateB = new Date(b.createdAt || b.lastModified || b.createdDate || 0);
    return dateB.getTime() - dateA.getTime(); // Absteigende Sortierung (neueste zuerst)
  });

  // Custom Option Component mit Lösch-Button
  const CustomOption: React.FC<OptionProps<ExtendedDraftOption, false>> = (props) => {
    const { data, innerRef, innerProps } = props;
    
    const handleDeleteClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDeleteDraft(data.draftData.id);
    };

    return (
      <div ref={innerRef} {...innerProps} className="flex items-center justify-between px-3 py-2 hover:bg-surface-hover cursor-pointer">
        <span className="flex-grow text-primary">{data.label}</span>
        {!isReadOnly && (
        <button
          onClick={handleDeleteClick}
          className="ml-2 p-1 hover:bg-red-600 text-muted hover:text-white rounded transition-colors group"
          title="Entwurf löschen"
        >
          <svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
            />
          </svg>
        </button>
        )}
      </div>
    );
  };

  // Optionen für react-select
  const draftSelectOptions: ExtendedDraftOption[] = sortedDrafts.map((draft: OfferDraftItem) => {
    // Versuche verschiedene Datumsfelder zu finden
    const dateStr = draft.createdAt || draft.lastModified || draft.createdDate;
    let formattedDate = 'Unbekanntes Datum';
    
    if (dateStr) {
      const formatted = formatDateCentral(dateStr, { locale: i18n.language, includeTime: true });
      if (formatted !== '—') {
        formattedDate = formatted;
      }
    }

    // Bessere Label-Formatierung
    const customerName = draft.buyerName || 'Unbekannter Kunde';
    const amount = draft.totalAmount ? `${draft.totalAmount.toFixed(2)} €` : 'Unbekannter Betrag';
    
    return {
      value: draft.id,
      label: `${customerName} - ${amount} (${formattedDate})`,
      draftData: draft
    };
  });

  // Aktuell ausgewählte Option für react-select
  const selectedDraftOption = draftSelectOptions.find(option => option.value === selectedDraftId);

  return (
    <div className="bg-surface-raised shadow-lg rounded-lg p-4 border border-hairline">
      <h2 className="text-lg font-semibold text-primary mb-3">{t('draft.useAsBasis')}</h2>
      <div className="flex items-center space-x-4">
        <div className="flex-grow">
          <Select<ExtendedDraftOption> 
            options={draftSelectOptions}
            value={selectedDraftOption || null}
            onChange={onSelectDraft}
            components={{ Option: CustomOption }}
            placeholder={t('draft.selectSaved')}
            isLoading={isLoading}
            isClearable={true}
            noOptionsMessage={() => isLoading ? t('messages.loadingDrafts') : t('messages.noOptionsMessage')}
            styles={{
              ...reactSelectTokenStyles,
              option: (base) => ({
                ...base,
                backgroundColor: 'transparent', // Wird von CustomOption gesteuert
                padding: 0, // Entferne Standard-Padding da CustomOption das handhabt
                cursor: 'pointer'
              }),
            }}
          />
          {error && <p className="text-[var(--tone-danger-fg)] text-sm mt-1">{error}</p>}
        </div>
      </div>
      
      {selectedDraftId && (
        <div className="mt-2 text-sm text-muted">
          <span className="font-medium text-primary">{t('messages.selectedDraft')}</span> {selectedDraftOption?.label}
        </div>
      )}
      
      {drafts.length === 0 && !isLoading && (
        <div className="mt-2 text-sm text-muted">
          {t('messages.noDraftsSaved')}
        </div>
      )}
    </div>
  );
};

export default OfferDraftSelector;